import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import { GridFour, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut, Buildings, MapTrifold } from '@phosphor-icons/react';

export const SpotPicker = ({ showId, onSelectionChange, staffMode = false }) => {
  const [spotMap, setSpotMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSpots, setSelectedSpots] = useState([]);
  // Per-type pricing — stalls and RV are billed INDEPENDENTLY because
  // riders commonly want stalls for the full weekend AND RV for only 1-2
  // nights (or vice-versa). Sharing a single pricingType/nights pair
  // caused a real bug where toggling RV to "per-day" silently dropped
  // the stall total.
  const [stallPricingType, setStallPricingType] = useState('weekend');
  const [stallNights, setStallNights] = useState(1);
  const [rvPricingType, setRvPricingType] = useState('weekend');
  const [rvNights, setRvNights] = useState(1);
  const [activeBarn, setActiveBarn] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'grid'
  const [activeLocation, setActiveLocation] = useState('main'); // 'main' or extra-loc id
  // Stall vs RV type-tab: only used when the show offers BOTH types of
  // accommodations. When the producer only set up one type we render that
  // type's barns directly and hide the tab strip entirely.
  const [activeSpotType, setActiveSpotType] = useState(null);

  // Zoom/pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const lastTouchDist = useRef(null);
  // Track the rendered size of the active-barn image so we can compute
  // px-accurate font-size for overlaid stall buttons (percent-based font
  // sizes don't exist in CSS).
  const [detailImgSize, setDetailImgSize] = useState({ w: 0, h: 0 });
  const detailImgRef = useRef(null);
  useEffect(() => {
    const el = detailImgRef.current;
    if (!el) return;
    const update = () => setDetailImgSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeBarn]);
  const computeSpotFont = (markerW, markerH) => {
    // Use the shorter rendered side as the basis so 2-3 digit numbers
    // always fit inside the cell.
    const w = (detailImgSize.w || 0) * (markerW / 100);
    const h = (detailImgSize.h || 0) * (markerH / 100);
    const side = Math.min(w, h);
    if (side <= 0) return '8px';
    // ~55% of shorter side works well for "XX" or "XXX", capped at 14px.
    const px = Math.max(5, Math.min(side * 0.55, 14));
    return `${px.toFixed(1)}px`;
  };

  useEffect(() => { loadSpotMap(); }, [showId]);

  // When switching between locations, exit any open barn drill-down.
  useEffect(() => { setActiveBarn(null); }, [activeLocation]);

  useEffect(() => {
    if (onSelectionChange) {
      // Stamp each spot with its CURRENT per-spot fee so downstream
      // consumers (EntrySuccess line items, payment line items, future
      // CSV exports) don't have to redivide the total. Pricing snapshot
      // (pricingType + nights) also gets attached so the office can see
      // whether a row was weekend-pricing or per-day at submit time.
      // Stalls and RV carry their OWN snapshots (v130 — independent pricing).
      const spotsWithPrice = selectedSpots.map((s) => {
        const isStall = s.type === 'stall';
        const pt = isStall ? stallPricingType : rvPricingType;
        const n = isStall ? stallNights : rvNights;
        return {
          ...s,
          fee: getSpotPrice(s.type),
          pricing_type: pt,
          nights: pt === 'per_day' ? n : null,
        };
      });
      onSelectionChange({
        spots: spotsWithPrice,
        stallPricingType, stallNights, rvPricingType, rvNights,
        totalFee: calcTotal(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpots, stallPricingType, stallNights, rvPricingType, rvNights, spotMap]);

  const loadSpotMap = async () => {
    try {
      const { data } = await api.get(`/shows/${showId}/spot-map`);
      setSpotMap(data);
      setStallNights(data.show_days || 1);
      setRvNights(data.show_days || 1);
      // Default to grid view if no facility map
      if (!data.facility_map_url) setViewMode('grid');
    } catch { setSpotMap(null); }
    finally { setLoading(false); }
  };

  const toggleSpot = (type, barn, row, spotNumber, status) => {
    if (status === 'paid' || status === 'pending') return;
    const locationId = activeLocation || 'main';
    const key = `${locationId}_${type}_${barn}_${row}_${spotNumber}`;
    const exists = selectedSpots.find(s => `${s.location_id || 'main'}_${s.type}_${s.barn}_${s.row}_${s.spot_number}` === key);
    if (exists) {
      setSelectedSpots(selectedSpots.filter(s => `${s.location_id || 'main'}_${s.type}_${s.barn}_${s.row}_${s.spot_number}` !== key));
    } else {
      setSelectedSpots([...selectedSpots, { type, barn, row, spot_number: spotNumber, location_id: locationId }]);
    }
  };

  const isSelected = (type, barn, row, spotNumber) => {
    const locationId = activeLocation || 'main';
    return selectedSpots.some(s => (s.location_id || 'main') === locationId && s.type === type && s.barn === barn && s.row === row && s.spot_number === spotNumber);
  };

  const getSpotPrice = (type) => {
    if (!spotMap) return 0;
    if (type === 'stall') {
      return stallPricingType === 'per_day' && spotMap.stall_price_per_day > 0
        ? spotMap.stall_price_per_day * stallNights : spotMap.stall_price;
    }
    return rvPricingType === 'per_day' && spotMap.rv_price_per_day > 0
      ? spotMap.rv_price_per_day * rvNights : spotMap.rv_price;
  };

  const calcTotal = () => selectedSpots.reduce((sum, s) => sum + getSpotPrice(s.type), 0);

  const selectedInBarn = (barnName, type) => selectedSpots.filter(s => s.barn === barnName && s.type === type).length;

  const getBarnAvailability = (barnName, type) => {
    const v = _resolveView();
    const map = type === 'stall' ? v?.stall_map : v?.rv_map;
    if (!map) return { total: 0, available: 0 };
    const barn = map.find(b => b.barn === barnName);
    if (!barn) return { total: 0, available: 0 };
    const total = barn.rows.reduce((s, r) => s + r.spots.length, 0);
    const available = barn.rows.reduce((s, r) => s + r.spots.filter(sp => sp.status === 'available').length, 0);
    return { total, available };
  };

  const findBarnData = (barnName, type) => {
    const v = _resolveView();
    const map = type === 'stall' ? v.stall_map : v.rv_map;
    if (!map || !map.length) return null;
    // 1) Exact match first
    let barn = map.find(b => b.barn === barnName);
    // 2) Case-insensitive + whitespace/punctuation-tolerant fallback.
    //    Producers commonly label markers "Barn A" while the stall chart
    //    they uploaded calls it "A" (or vice-versa). Normalize both sides
    //    so the tap-to-drill still finds the right chart.
    if (!barn && barnName) {
      const norm = (s) => (s || '').toString().toLowerCase().replace(/[\s\-_/.]+/g, '').replace(/^barn/, '');
      const target = norm(barnName);
      if (target) {
        barn = map.find(b => norm(b.barn) === target)
            || map.find(b => norm(b.barn).endsWith(target) || target.endsWith(norm(b.barn)));
      }
    }
    // 3) Last resort — when there's only ONE barn in the chart, assume
    //    every marker refers to it (single-barn shows are common).
    if (!barn && map.length === 1) barn = map[0];
    return barn ? { ...barn, type } : null;
  };

  // Helper used by the callbacks so they always see the currently-active
  // location's data even before the render-time `view` constant is created.
  const _resolveView = () => {
    if (!spotMap) return { stall_map: [], rv_map: [] };
    if (activeLocation === 'main') return spotMap;
    const loc = (spotMap.extra_locations || []).find(l => l.id === activeLocation);
    if (!loc) return spotMap;
    return {
      stall_map: loc.type === 'stall' ? loc.map : [],
      rv_map: loc.type === 'rv' ? loc.map : [],
    };
  };

  // Zoom
  const zoomIn = () => setZoom(z => Math.min(z + 0.25, 4));
  const zoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5));
  const resetView = () => { fitToViewport(); };

  // Fit the entire map inside the rendered viewport on first load. Riders
  // shouldn't have to pan around just to see where the barns are — the
  // whole map should be visible by default and they can zoom in from there.
  const facilityImgRef = useRef(null);
  const fitToViewport = () => {
    const img = facilityImgRef.current || detailImgRef.current;
    const cont = containerRef.current;
    if (!img || !cont || !img.naturalWidth || !img.naturalHeight) return;
    const cw = cont.clientWidth;
    const ch = cont.clientHeight || 500;
    // Fit the image to whichever container dimension is the tightest.
    // Allow upscaling on wide desktop containers (up to 4x — same cap as
    // manual zoom). Previously hard-capped at 1.0 → narrow images sat
    // tiny in a wide desktop viewport while looking fine on mobile.
    const fit = Math.min(cw / img.naturalWidth, ch / img.naturalHeight, 4);
    setZoom(Math.max(0.1, fit));
    setPan({ x: 0, y: 0 });
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.min(Math.max(z + (e.deltaY > 0 ? -0.1 : 0.1), 0.5), 4));
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [isDragging, dragStart]);
  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
    }
  };
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && lastTouchDist.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      setZoom(z => Math.min(Math.max(z * (dist / lastTouchDist.current), 0.5), 4));
      lastTouchDist.current = dist;
    } else if (e.touches.length === 1 && isDragging) {
      setPan({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
    }
  }, [isDragging, dragStart]);
  const handleTouchEnd = () => { setIsDragging(false); lastTouchDist.current = null; };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  if (loading) return null;
  const extraLocations = spotMap?.extra_locations || [];
  const activeExtra = extraLocations.find(l => l.id === activeLocation) || null;
  // Derived view — swap stall/rv map + detail map + markers based on which
  // location tab is active. Facility map + barn-markers only belong to "main".
  const view = activeExtra ? {
    stall_map: activeExtra.type === 'stall' ? activeExtra.map : [],
    rv_map: activeExtra.type === 'rv' ? activeExtra.map : [],
    stall_detail_map_url: activeExtra.type === 'stall' ? activeExtra.detail_map_url : null,
    rv_detail_map_url: activeExtra.type === 'rv' ? activeExtra.detail_map_url : null,
    stall_detail_markers: activeExtra.type === 'stall' ? (activeExtra.markers || []) : [],
    rv_detail_markers: activeExtra.type === 'rv' ? (activeExtra.markers || []) : [],
    facility_map_url: null,   // extras skip the facility map
    barn_markers: [],
    has_stall_chart: activeExtra.type === 'stall' && (activeExtra.map || []).length > 0,
    has_rv_chart: activeExtra.type === 'rv' && (activeExtra.map || []).length > 0,
  } : (spotMap || {
    stall_map: [], rv_map: [], stall_detail_map_url: null, rv_detail_map_url: null,
    stall_detail_markers: [], rv_detail_markers: [], facility_map_url: null,
    barn_markers: [], has_stall_chart: false, has_rv_chart: false,
  });

  if (!spotMap || (!spotMap.has_stall_chart && !spotMap.has_rv_chart && extraLocations.length === 0)) return null;

  const stallCount = selectedSpots.filter(s => s.type === 'stall').length;
  const rvCount = selectedSpots.filter(s => s.type === 'rv').length;
  const hasPerDay = (spotMap.stall_price_per_day > 0 || spotMap.rv_price_per_day > 0);
  const hasWeekend = (spotMap.stall_price > 0 || spotMap.rv_price > 0);
  const hasFacilityMap = !!view.facility_map_url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // All barns (stalls + RV combined). Filtered by activeSpotType when set.
  const allBarnsUnfiltered = [
    ...(view.stall_map || []).map(b => ({ ...b, type: 'stall' })),
    ...(view.rv_map || []).map(b => ({ ...b, type: 'rv' }))
  ];
  const hasStallBarns = (view.stall_map || []).length > 0;
  const hasRvBarns = (view.rv_map || []).length > 0;
  const showTypeTabs = hasStallBarns && hasRvBarns;
  // Default the type tab to whichever exists; if both, default to stalls.
  // Compute on render so it stays in sync with location changes.
  const effectiveType = activeSpotType || (hasStallBarns ? 'stall' : (hasRvBarns ? 'rv' : null));
  const allBarns = showTypeTabs && effectiveType
    ? allBarnsUnfiltered.filter(b => b.type === effectiveType)
    : allBarnsUnfiltered;

  const renderBarnGrid = (barn, type) => {
    const pricePerSpot = getSpotPrice(type);
    return (
      <div className="space-y-1.5">
        {barn.rows.map((row, ridx) => (
          <div key={ridx} className="flex items-center gap-1.5">
            <span className="text-[10px] text-[#0096FF] font-bold w-5 flex-shrink-0 text-right">{row.label}</span>
            <div className="flex gap-1 flex-wrap">
              {row.spots.map((spot) => {
                const selected = isSelected(type, barn.barn, row.label, spot.number);
                const isPaid = spot.status === 'paid';
                const isPending = spot.status === 'pending' && !selected;
                return (
                  <button
                    key={spot.number}
                    onClick={(e) => { e.stopPropagation(); toggleSpot(type, barn.barn, row.label, spot.number, spot.status); }}
                    disabled={isPaid || isPending}
                    title={`${barn.barn} Row ${row.label} #${spot.number}: ${isPaid ? 'Taken' : isPending ? 'Reserved' : selected ? 'Selected' : 'Available'}`}
                    data-testid={`spot-${type}-${barn.barn}-${row.label}-${spot.number}`}
                    className={`w-8 h-8 rounded flex items-center justify-center text-[9px] font-bold transition-all border ${
                      isPaid ? 'bg-[#FF3B30]/15 border-[#FF3B30]/30 text-[#FF3B30]/60 cursor-not-allowed'
                      : isPending ? 'bg-[#FF9500]/15 border-[#FF9500]/30 text-[#FF9500]/60 cursor-not-allowed'
                      : selected ? 'bg-[#0096FF]/25 border-[#0096FF] text-[#0096FF] ring-1 ring-[#0096FF]/50'
                      : 'bg-[#1A3A1A] border-[#2A5A2A] text-[#34C759] hover:bg-[#34C759]/20 hover:border-[#34C759] cursor-pointer'
                    }`}
                  >
                    {spot.number}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {pricePerSpot > 0 && <p className="text-[10px] text-[#555] mt-1">${pricePerSpot.toFixed(2)} per {type === 'stall' ? 'stall' : 'spot'}</p>}
      </div>
    );
  };

  return (
    <div className="lg:col-span-2 bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="spot-picker">
      {/* Location tabs (only when extra locations exist) */}
      {extraLocations.length > 0 && (
        <div className="-mx-5 -mt-5 mb-4 px-5 pt-3 pb-0 border-b border-[#262626] bg-[#0F0F0F] rounded-t-lg overflow-x-auto" data-testid="spot-picker-location-tabs">
          <div className="flex items-end gap-1 min-w-max">
            <button
              onClick={() => setActiveLocation('main')}
              className={`px-3 py-2 text-xs font-bold transition-colors rounded-t-md ${activeLocation === 'main' ? 'bg-[#161616] text-white border-t border-l border-r border-[#262626]' : 'text-[#9E9E9E] hover:text-white'}`}
              data-testid="spot-loc-tab-main"
            >
              Main
            </button>
            {extraLocations.map((loc) => (
              <button
                key={loc.id}
                onClick={() => setActiveLocation(loc.id)}
                className={`px-3 py-2 text-xs font-bold transition-colors rounded-t-md ${activeLocation === loc.id ? 'bg-[#161616] text-white border-t border-l border-r border-[#262626]' : 'text-[#9E9E9E] hover:text-white'}`}
                data-testid={`spot-loc-tab-${loc.id}`}
              >
                {loc.name}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] ${loc.type === 'stall' ? 'bg-[#0096FF]/20 text-[#0096FF]' : 'bg-[#FF9500]/20 text-[#FF9500]'}`}>
                  {loc.type === 'stall' ? 'STALL' : 'RV'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stall vs RV type tabs — only shown when the show offers both types
          on the active location. Producers can show only stalls, only RV, or
          both; the tab strip hides itself unless both exist. */}
      {showTypeTabs && !activeBarn && (
        <div className="flex items-center gap-1 mb-3 border-b border-[#262626]" data-testid="spot-picker-type-tabs">
          <button
            onClick={() => setActiveSpotType('stall')}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold border-b-2 transition-colors -mb-px ${effectiveType === 'stall' ? 'border-[#0096FF] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="spot-type-tab-stall"
          >
            <Buildings size={12} /> Stalls
            {(view.stall_map || []).length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] bg-[#0096FF]/20 text-[#0096FF]">
                {(view.stall_map || []).reduce((s, b) => s + b.rows.reduce((s2, r) => s2 + r.spots.length, 0), 0)}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveSpotType('rv')}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold border-b-2 transition-colors -mb-px ${effectiveType === 'rv' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="spot-type-tab-rv"
          >
            <MapTrifold size={12} /> RV Hookups
            {(view.rv_map || []).length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] bg-[#FF9500]/20 text-[#FF9500]">
                {(view.rv_map || []).reduce((s, b) => s + b.rows.reduce((s2, r) => s2 + r.spots.length, 0), 0)}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <GridFour size={18} className="text-[#0096FF]" />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Select Your Spots</h3>
        </div>
        <div className="flex items-center gap-1">
          {hasFacilityMap && (
            <>
              <button onClick={() => { setViewMode('map'); setActiveBarn(null); resetView(); }}
                className={`px-2 py-1 rounded text-[10px] font-semibold transition-all ${viewMode === 'map' ? 'bg-[#34C759]/20 text-[#34C759] border border-[#34C759]/30' : 'text-[#555] hover:text-white'}`}
                data-testid="view-map-btn">
                <MapTrifold size={12} className="inline mr-1" />Map
              </button>
              <button onClick={() => { setViewMode('grid'); setActiveBarn(null); }}
                className={`px-2 py-1 rounded text-[10px] font-semibold transition-all ${viewMode === 'grid' ? 'bg-[#0096FF]/20 text-[#0096FF] border border-[#0096FF]/30' : 'text-[#555] hover:text-white'}`}
                data-testid="view-grid-btn">
                <GridFour size={12} className="inline mr-1" />Grid
              </button>
              <div className="w-px h-4 bg-[#262626] mx-1"></div>
            </>
          )}
          <button onClick={zoomOut} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 transition-colors" data-testid="zoom-out-btn">
            <MagnifyingGlassMinus size={14} />
          </button>
          <span className="text-[10px] text-[#555] w-10 text-center" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
          <button onClick={zoomIn} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 transition-colors" data-testid="zoom-in-btn">
            <MagnifyingGlassPlus size={14} />
          </button>
          <button onClick={resetView} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 transition-colors" data-testid="zoom-reset-btn">
            <ArrowsOut size={14} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-[10px] text-[#555] mb-3">
        <span>Scroll/pinch to zoom, drag to pan</span>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#1A3A1A] border border-[#2A5A2A] inline-block"></span>Available</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#0096FF]/25 border border-[#0096FF] inline-block"></span>Selected</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#FF3B30]/15 border border-[#FF3B30]/30 inline-block"></span>Taken</span>
        </div>
      </div>

      {/* Pricing toggle (hidden in staff mode) — split per spot type so
          stalls and RV can be billed independently (e.g. stalls for the
          weekend + RV for only 2 nights). Only renders when the show
          offers BOTH weekend and per-day pricing for that spot type.
          When the producer has disabled per-day pricing via
          `stall_per_day_disabled`, the toggle is skipped entirely so the
          rider sees only the flat weekend rate. */}
      {!staffMode && spotMap.stall_price > 0 && spotMap.stall_price_per_day > 0 && !spotMap.stall_per_day_disabled && (
        <div className="mb-3" data-testid="stall-pricing-section">
          <p className="text-[11px] text-[#9E9E9E] font-semibold mb-1.5 uppercase tracking-wide">Stalls</p>
          <div className="flex gap-2 mb-2" data-testid="stall-pricing-toggle">
            <button onClick={() => setStallPricingType('weekend')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${stallPricingType === 'weekend' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'}`}
              data-testid="stall-pricing-weekend">Weekend (${spotMap.stall_price})</button>
            <button onClick={() => setStallPricingType('per_day')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${stallPricingType === 'per_day' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'}`}
              data-testid="stall-pricing-perday">Per Night (${spotMap.stall_price_per_day})</button>
          </div>
          {stallPricingType === 'per_day' && (
            <div className="flex items-center gap-3 ml-1">
              <label className="text-xs text-[#9E9E9E]">Nights:</label>
              <button onClick={() => setStallNights(Math.max(1, stallNights - 1))} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm font-bold" data-testid="stall-nights-decrease">-</button>
              <span className="text-white font-bold text-sm w-6 text-center" data-testid="stall-nights-count">{stallNights}</span>
              <button onClick={() => setStallNights(Math.min(spotMap.show_days || 7, stallNights + 1))} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm font-bold" data-testid="stall-nights-increase">+</button>
              <span className="text-[10px] text-[#555]">of {spotMap.show_days} day{spotMap.show_days > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
      {!staffMode && spotMap.rv_price > 0 && spotMap.rv_price_per_day > 0 && (
        <div className="mb-3" data-testid="rv-pricing-section">
          <p className="text-[11px] text-[#9E9E9E] font-semibold mb-1.5 uppercase tracking-wide">RV / Electric</p>
          <div className="flex gap-2 mb-2" data-testid="rv-pricing-toggle">
            <button onClick={() => setRvPricingType('weekend')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${rvPricingType === 'weekend' ? 'bg-[#AF52DE] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'}`}
              data-testid="rv-pricing-weekend">Weekend (${spotMap.rv_price})</button>
            <button onClick={() => setRvPricingType('per_day')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-all ${rvPricingType === 'per_day' ? 'bg-[#AF52DE] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E]'}`}
              data-testid="rv-pricing-perday">Per Night (${spotMap.rv_price_per_day})</button>
          </div>
          {rvPricingType === 'per_day' && (
            <div className="flex items-center gap-3 ml-1">
              <label className="text-xs text-[#9E9E9E]">Nights:</label>
              <button onClick={() => setRvNights(Math.max(1, rvNights - 1))} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm font-bold" data-testid="rv-nights-decrease">-</button>
              <span className="text-white font-bold text-sm w-6 text-center" data-testid="rv-nights-count">{rvNights}</span>
              <button onClick={() => setRvNights(Math.min(spotMap.show_days || 7, rvNights + 1))} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm font-bold" data-testid="rv-nights-increase">+</button>
              <span className="text-[10px] text-[#555]">of {spotMap.show_days} day{spotMap.show_days > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}

      {/* === MAP VIEW with STALLS OVERLAID === */}
      {viewMode === 'map' && hasFacilityMap && !activeBarn && (
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-lg border border-[#1A1A1A] bg-[#080808] select-none"
          style={{ height: '500px', cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}
          data-testid="spot-map-viewport"
        >
          <div style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
            position: 'relative',
            display: 'inline-block',
            lineHeight: 0,
          }}>
            <img
              ref={facilityImgRef}
              src={`${backendUrl}${view.facility_map_url}`}
              alt="Facility Map"
              className="max-w-none"
              style={{ display: 'block', border: 0, padding: 0, margin: 0 }}
              draggable={false}
              data-testid="facility-map-image"
              onLoad={fitToViewport}
            />
            {/* Overlay layer pinned EXACTLY to the image bounds. Markers
                position with percentages of this overlay so they always
                align with the producer-side coords regardless of any
                parent-div padding/border quirks. */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
            {/* Overlay clickable barn pins. Each barn marker is a TAP-TO-DRILL
                button — riders click anywhere within the barn region and
                we open that barn's stall picker. When the marker is big
                enough to host mini-stall buttons inline AND the producer
                wants in-place picking, we render mini-stalls; otherwise we
                show a translucent badge with the barn name + open count. */}
            {(view.barn_markers || [])
              .filter(m => !showTypeTabs || !effectiveType || m.type === effectiveType)
              .map((marker, mi) => {
              const hasArea = Number(marker.w) > 0 && Number(marker.h) > 0;
              const barnData = findBarnData(marker.barn, marker.type);
              const avail = barnData ? getBarnAvailability(marker.barn, marker.type) : null;
              const mySelected = barnData ? selectedInBarn(marker.barn, marker.type) : 0;
              const drillIn = () => {
                if (barnData) setActiveBarn({ ...barnData, idx: mi });
              };

              // Point pin (no width/height) — render a circular tap target.
              if (!hasArea) {
                return (
                  <button
                    key={mi}
                    onClick={(e) => { e.stopPropagation(); drillIn(); }}
                    className="absolute flex items-center justify-center rounded-full bg-[#FF9500] text-white text-[10px] font-bold border-2 border-white shadow-lg hover:scale-110 transition-transform cursor-pointer"
                    style={{ left: `${marker.x}%`, top: `${marker.y}%`, width: '28px', height: '28px', transform: 'translate(-50%, -50%)', pointerEvents: 'auto' }}
                    title={`Tap to pick spots in ${marker.barn}`}
                    data-testid={`map-pin-${mi}`}
                  >
                    {(marker.barn || '?').slice(0, 2)}
                  </button>
                );
              }

              // Rectangle region — tap anywhere inside drills in; show label badge.
              return (
                <button
                  key={mi}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); drillIn(); }}
                  disabled={!barnData}
                  className={`absolute rounded transition-colors text-left ${
                    barnData
                      ? `${mySelected > 0 ? 'border-2 border-[#0096FF] bg-[#0096FF]/15' : 'border-2 border-dashed border-[#FF9500]/70 bg-[#FF9500]/5 hover:bg-[#FF9500]/15'} cursor-pointer`
                      : 'border-2 border-dashed border-red-500/60 bg-red-500/5 cursor-not-allowed'
                  }`}
                  style={{ left: `${marker.x}%`, top: `${marker.y}%`, width: `${marker.w}%`, height: `${marker.h}%`, padding: 0, margin: 0, boxSizing: 'border-box', pointerEvents: 'auto' }}
                  title={barnData ? `Tap to pick spots in ${marker.barn}` : `Barn "${marker.barn}" — no stall chart configured`}
                  data-testid={`map-overlay-${mi}`}
                >
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#0A0A0A]/90 flex items-center gap-1" style={{ fontSize: '10px' }}>
                    <span className="font-bold" style={{ color: marker.type === 'rv' ? '#AF52DE' : '#FF9500' }}>{marker.barn}</span>
                    {barnData && avail && (
                      <span className="text-white/80 font-semibold">{avail.available}/{avail.total}</span>
                    )}
                    {mySelected > 0 && (
                      <span className="text-[#0096FF] font-bold">+{mySelected}</span>
                    )}
                  </div>
                </button>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* Barn selector cards (shown if no pins, or in grid mode, or alongside map) */}
      {!activeBarn && (
        <div className={viewMode === 'map' && hasFacilityMap ? 'mt-3' : ''}>
          {(view.barn_markers || []).length > 0 && viewMode === 'map' && hasFacilityMap ? (
            <p className="text-xs text-[#9E9E9E] mb-2">Tap a barn on the map to open its stall picker, or choose below:</p>
          ) : (
            <p className="text-xs text-[#9E9E9E] mb-2">Select a barn to pick your spots:</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="barn-selector">
            {allBarns.map((barn, idx) => {
              const total = barn.rows.reduce((s, r) => s + r.spots.length, 0);
              const available = barn.rows.reduce((s, r) => s + r.spots.filter(sp => sp.status === 'available').length, 0);
              const mySelected = selectedInBarn(barn.barn, barn.type);
              return (
                <button
                  key={`${barn.type}-${barn.barn}`}
                  onClick={() => { setActiveBarn({ ...barn, idx }); resetView(); }}
                  data-testid={`barn-select-${barn.type}-${idx}`}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    mySelected > 0 ? 'bg-[#0096FF]/10 border-[#0096FF]/40' : 'bg-[#0A0A0A] border-[#1A1A1A] hover:border-[#FF9500]/40'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Buildings size={14} className="text-[#FF9500]" />
                    <span className="text-sm font-bold text-white truncate" style={{ fontFamily: 'Outfit' }}>{barn.barn}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-[#555]">{available}/{total} open</span>
                    {mySelected > 0 && (
                      <span className="text-[10px] text-[#0096FF] font-bold">{mySelected} selected</span>
                    )}
                  </div>
                  <span className="text-[10px] text-[#555] capitalize">{barn.type === 'rv' ? 'RV' : 'Stalls'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* === ACTIVE BARN DETAIL VIEW === */}
      {activeBarn && (
        <div data-testid="barn-detail-view">
          <button
            onClick={() => setActiveBarn(null)}
            className="flex items-center gap-1 text-xs text-[#0096FF] hover:text-white mb-3 transition-colors"
            data-testid="barn-back-btn"
          >
            &larr; Back to all barns
          </button>
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Buildings size={18} weight="duotone" className="text-[#FF9500]" />
              <h4 className="text-base font-bold text-[#FF9500]" style={{ fontFamily: 'Outfit' }} data-testid="active-barn-name">{activeBarn.barn}</h4>
              <span className="text-xs text-[#555] capitalize">{activeBarn.type === 'rv' ? 'RV Spots' : 'Stalls'}</span>
            </div>

            {/* Per-barn / show-level detail map with clickable stalls overlaid.
                Priority: per-barn detail_map_url > show-level stall_detail_map_url.
                If a barn has its own detail map but no markers yet, we show
                the image as a static reference above the flat grid below. */}
            {(() => {
              // 1) Per-barn map preferred
              const perBarnUrl = activeBarn.detail_map_url;
              const perBarnMarkers = (activeBarn.detail_markers || []).map(m => ({ ...m, barn: activeBarn.barn, type: activeBarn.type }));
              // 2) Fall back to show-level map (filtered to this barn)
              const showUrl = activeBarn.type === 'stall' ? view.stall_detail_map_url : view.rv_detail_map_url;
              const showMarkers = activeBarn.type === 'stall' ? (view.stall_detail_markers || []) : (view.rv_detail_markers || []);

              const usePerBarn = !!perBarnUrl;
              const dmUrl = usePerBarn ? perBarnUrl : showUrl;
              // Pick the marker set. Priority:
              //  a) per-barn floor plan + per-barn markers (coords relative to per-barn image)
              //  b) NO per-barn floor plan BUT per-barn markers exist + show-level map
              //     exists → the per-barn markers were detected against the show-level
              //     map (coords relative to it), so render them on the show-level map.
              //  c) Show-level barn-region marker (auto-tile fallback).
              let barnMarkers;
              if (usePerBarn) {
                barnMarkers = perBarnMarkers;
              } else if (perBarnMarkers.length > 1 && showUrl) {
                barnMarkers = perBarnMarkers;
              } else {
                barnMarkers = showMarkers.filter(m => m.barn === activeBarn.barn);
              }

              if (!dmUrl) return null;

              const perCell = barnMarkers.length > 1;
              const singleMarker = !perCell && barnMarkers.length === 1 ? barnMarkers[0] : null;
              const allSpots = activeBarn.rows.flatMap(r => r.spots.map(s => ({ ...s, row: r.label })));
              // If per-barn image exists but no markers yet → show image as a
              // reference banner; the flat-grid picker below stays clickable.
              const referenceOnly = barnMarkers.length === 0;

              return (
                <div
                  ref={containerRef}
                  className="relative overflow-hidden rounded-lg border border-[#1A1A1A] bg-[#080808] select-none mb-3 h-[260px] sm:h-[340px] md:h-[400px]"
                  style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
                  onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}
                  data-testid="detail-map-viewport"
                >
                  <div style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: '0 0',
                    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                    position: 'relative',
                    // CRITICAL: inline-block shrinks the wrapper to the image's
                    // natural pixel dimensions. Without this, the wrapper
                    // expands to fill the parent (block default), and the
                    // markers' percentage positions get computed against the
                    // wrong width — they drift off the image on wide desktops.
                    display: 'inline-block',
                  }}>
                    <img src={`${backendUrl}${dmUrl}`} alt="Detail Map" ref={detailImgRef} style={{ display: 'block' }} draggable={false} data-testid="detail-map-image" onLoad={fitToViewport} />
                    {/* Per-cell markers: render each marker as its own button at its exact position */}
                    {perCell && barnMarkers.map((m, mi) => {
                      // Match this marker to its spot by stall number (label == start_number).
                      // Markers are generated in reading order matching row order.
                      const rowIdx = mi;
                      const row = activeBarn.rows[rowIdx];
                      const spot = row?.spots?.[0];
                      if (!spot) return null;
                      const sel = isSelected(activeBarn.type, activeBarn.barn, row.label, spot.number);
                      const isPaid = spot.status === 'paid';
                      const isPending = spot.status === 'pending' && !sel;
                      return (
                        <button key={`cell-${mi}`}
                          onClick={(e) => { e.stopPropagation(); toggleSpot(activeBarn.type, activeBarn.barn, row.label, spot.number, spot.status); }}
                          disabled={isPaid || isPending}
                          title={`#${spot.number}`}
                          data-testid={`spot-${activeBarn.type}-${activeBarn.barn}-${row.label}-${spot.number}`}
                          className={`absolute flex items-center justify-center font-bold transition-all border rounded-[1px] ${
                            isPaid ? 'bg-[#FF3B30]/40 border-[#FF3B30] text-white cursor-not-allowed'
                            : isPending ? 'bg-[#FF9500]/40 border-[#FF9500] text-white cursor-not-allowed'
                            : sel ? 'bg-[#0096FF]/60 border-[#0096FF] text-white ring-2 ring-[#0096FF]'
                            : 'bg-[#34C759]/40 border-[#34C759] text-white hover:bg-[#34C759]/70 cursor-pointer'
                          }`}
                          style={{
                            left: `${m.x}%`,
                            top: `${m.y}%`,
                            width: `${m.w}%`,
                            height: `${m.h}%`,
                            fontSize: computeSpotFont(m.w, m.h),
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >{spot.number}</button>
                      );
                    })}
                    {/* Single barn-level marker: subdivide into a wrapping grid of spot buttons */}
                    {singleMarker && (
                      <div className="absolute" style={{ left: `${singleMarker.x}%`, top: `${singleMarker.y}%`, width: `${singleMarker.w}%`, height: `${singleMarker.h}%` }}>
                        <div className="w-full h-full flex flex-wrap content-start gap-[1px] p-[1px] overflow-hidden">
                          {allSpots.map((spot) => {
                            const sel = isSelected(activeBarn.type, activeBarn.barn, spot.row, spot.number);
                            const isPaid = spot.status === 'paid';
                            const isPending = spot.status === 'pending' && !sel;
                            return (
                              <button key={`${spot.row}-${spot.number}`}
                                onClick={(e) => { e.stopPropagation(); toggleSpot(activeBarn.type, activeBarn.barn, spot.row, spot.number, spot.status); }}
                                disabled={isPaid || isPending}
                                title={`#${spot.number}`}
                                data-testid={`spot-${activeBarn.type}-${activeBarn.barn}-${spot.row}-${spot.number}`}
                                className={`rounded-[1px] flex items-center justify-center font-bold transition-all border ${
                                  isPaid ? 'bg-[#FF3B30]/30 border-[#FF3B30]/40 text-[#FF3B30]/80 cursor-not-allowed'
                                  : isPending ? 'bg-[#FF9500]/30 border-[#FF9500]/40 text-[#FF9500]/80 cursor-not-allowed'
                                  : sel ? 'bg-[#0096FF]/40 border-[#0096FF] text-white ring-1 ring-[#0096FF]/60'
                                  : 'bg-[#1A3A1A]/80 border-[#2A5A2A]/60 text-[#34C759] hover:bg-[#34C759]/30 cursor-pointer'
                                }`}
                                style={{
                                  width: `${Math.max(8, Math.floor(100 / Math.max(Math.ceil(Math.sqrt(allSpots.length)), 1)) - 1)}%`,
                                  aspectRatio: '1',
                                  // Scale font with rendered cell size rather
                                  // than spot-count heuristics so 2-3 digit
                                  // numbers fit even in dense barns.
                                  fontSize: `clamp(5px, ${(80 / Math.max(Math.sqrt(allSpots.length), 1)).toFixed(1)}%, 11px)`,
                                  lineHeight: 1,
                                  padding: 0,
                                }}
                              >{spot.number}</button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* No markers at all for this barn: fall back to full-width grid.
                        Skipped for per-barn referenceOnly so the image stays
                        visible as a banner and the proper fallback grid
                        renders below. */}
                    {!perCell && !singleMarker && !referenceOnly && (
                      <div className="absolute inset-0 flex flex-wrap content-start gap-[1px] p-2">
                        {allSpots.map((spot) => {
                          const sel = isSelected(activeBarn.type, activeBarn.barn, spot.row, spot.number);
                          const isPaid = spot.status === 'paid';
                          const isPending = spot.status === 'pending' && !sel;
                          return (
                            <button key={`${spot.row}-${spot.number}`}
                              onClick={(e) => { e.stopPropagation(); toggleSpot(activeBarn.type, activeBarn.barn, spot.row, spot.number, spot.status); }}
                              disabled={isPaid || isPending}
                              title={`#${spot.number}`}
                              className={`w-8 h-8 rounded flex items-center justify-center text-[9px] font-bold transition-all border ${
                                isPaid ? 'bg-[#FF3B30]/30 border-[#FF3B30]/40 text-[#FF3B30]/80'
                                : isPending ? 'bg-[#FF9500]/30 border-[#FF9500]/40 text-[#FF9500]/80'
                                : sel ? 'bg-[#0096FF]/40 border-[#0096FF] text-white'
                                : 'bg-[#1A3A1A]/80 border-[#2A5A2A]/60 text-[#34C759] hover:bg-[#34C759]/30 cursor-pointer'
                              }`}
                            >{spot.number}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Fallback grid — renders when there's no detail map at all OR
                when a per-barn image was uploaded but no markers are placed
                yet (referenceOnly). Shows the standard row-based picker. */}
            {(() => {
              const hasShowMap = (activeBarn.type === 'stall' && view.stall_detail_map_url) || (activeBarn.type === 'rv' && view.rv_detail_map_url);
              const hasPerBarnMarkers = activeBarn.detail_map_url && (activeBarn.detail_markers || []).length > 0;
              const shouldShowGrid = (!activeBarn.detail_map_url && !hasShowMap) || (activeBarn.detail_map_url && !hasPerBarnMarkers);
              if (!shouldShowGrid) return null;
              return (
            <div
              ref={containerRef}
              className="overflow-hidden rounded-lg border border-[#1A1A1A] bg-[#080808] select-none"
              style={{ minHeight: '200px', maxHeight: '400px', cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
              onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}
              data-testid="barn-grid-viewport"
            >
              <div style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                padding: '16px',
                minWidth: 'max-content'
              }}>
                {renderBarnGrid(activeBarn, activeBarn.type)}
              </div>
            </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Per-barn occupant list — riders use this to pick stalls next
          to their friends. Only shows when a barn is open and has at
          least one reservation. */}
      {activeBarn && (activeBarn.occupants || []).length > 0 && (
        <div
          className="mt-3 bg-[#0F0F0F] border border-[#262626] rounded-lg p-4"
          data-testid="barn-occupants-list"
        >
          <div className="flex items-baseline justify-between mb-2">
            <h5 className="text-xs uppercase tracking-wider font-bold text-[#9E9E9E]">
              Who&apos;s in {activeBarn.barn}
            </h5>
            <span className="text-[10px] text-[#555]">
              {activeBarn.occupants.length} reserved
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
            {activeBarn.occupants.map((occ, i) => (
              <div
                key={`${occ.row}-${occ.spot_number}-${i}`}
                className="flex items-center gap-2 text-[11px] py-0.5"
                data-testid={`occupant-${occ.row}-${occ.spot_number}`}
              >
                <span className="font-mono text-[#FF9500] font-bold flex-shrink-0 w-12">
                  {occ.row}-{occ.spot_number}
                </span>
                <span className="text-white truncate">
                  {occ.rider_name || <span className="text-[#555] italic">Reserved</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Selection summary */}
      {selectedSpots.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#262626]" data-testid="spot-selection-summary">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#9E9E9E]">
              {stallCount > 0 && <span>{stallCount} stall{stallCount > 1 ? 's' : ''}</span>}
              {stallCount > 0 && rvCount > 0 && <span> + </span>}
              {rvCount > 0 && <span>{rvCount} RV spot{rvCount > 1 ? 's' : ''}</span>}
            </span>
            <span className="text-[#0096FF] font-bold" data-testid="spot-total-fee">{staffMode ? `${selectedSpots.length} selected` : `$${calcTotal().toFixed(2)}`}</span>
          </div>
        </div>
      )}
    </div>
  );
};
